<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end align-center">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Busca productos por nombre"
          solo
          hide-details
          rounded
          flat
          dense/>
        <div class="px-3">
          <v-tooltip left>
            <template
              v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                :href="exportRoute"
                target="_blank"
                dark
                fab
                x-small
                depressed
                color="primary">
                <v-icon color="white">mdi-application-export</v-icon>
              </v-btn>
            </template>
            <span>Exportar inventario a CSV</span>
          </v-tooltip>
          <v-tooltip left>
            <template
              v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                @click="cleanDialog = true"
                dark
                fab
                x-small
                depressed
                color="secondary"
                class="ml-3">
                <v-icon color="white">mdi-sync</v-icon>
              </v-btn>
            </template>
            <span>Limpiar inventario</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <div class="px-3">
      <v-card class="px-3">
        <v-row>
          <v-col
            class="d-flex justify-space-between">
            <div class="subtitle-2">
              Valor del inventario físico:
            </div>
            <div class="primary--text">
              {{ calculateTotal('amountQuantityAvailable') | toFixNumber(2) | prefix('$') | numberWithComma }}
            </div>
          </v-col>
          <v-col
            class="d-flex justify-space-between">
            <div class="subtitle-2">
              Valor del inventario teórico:
            </div>
            <div class="primary--text">
              {{ calculateTotal('amountQuantityCalculated') | toFixNumber(2) | prefix('$') | numberWithComma }}
            </div>
          </v-col>
          <v-col
            class="d-flex justify-space-between">
            <div class="subtitle-2">
              Valor de la diferencia:
            </div>
            <div class="primary--text">
              {{ calculateTotal('amountDifference') | toFixNumber(2) | prefix('$') | numberWithComma }}
            </div>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <div class="px-3 pt-5">
      <v-skeleton-loader v-if="overlay" type="button" />
      <v-btn-toggle
        v-else
        v-model="favorites"
        rounded
        mandatory>
        <v-btn
          small
          class="white--text"
          :outlined="favorites !== 0"
          color="primary">
          Todos
        </v-btn>
        <v-btn
          class="white--text"
          small
          :outlined="favorites !== 1"
          color="primary">
          Favoritos
        </v-btn>
      </v-btn-toggle>
    </div>

    <v-flex xs12>
      <v-skeleton-loader v-show="overlay" type="table" />
      <v-form v-show="!overlay" ref="inventory" v-model="isValid">
        <v-data-table
          :loading-text="table.title"
          :server-items-length="table.total"
          :headers="headers"
          :items="table.data"
          :options="table.options"
          :sort-by="sortAttrName"
          @update:options="updateTable"
          @update:sort-by="sortByTable"
          @update:sort-desc="sortDescTable"
          :footer-props="table.footer"
          class="elevation-1 fixed-header horizontal-scroll">
          <template v-slot:top>
            <div class="px-3 filters">
              <v-row>
                <v-col cols="1">
                  <span class="subtitle-2 grey--text">
                    Filtrar por:
                  </span>
                </v-col>
                <v-col cols="2">
                  <v-select
                    v-model="category"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Departamento"
                    hide-details
                    @change="onChangeCategory"
                  />
                </v-col>
                <v-col cols="2">
                  <v-btn
                    text
                    color="primary"
                    rounded
                    small
                    :to="{ name: 'InventoryHistoric' }">
                    Ver histórico
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </template>
          <template v-slot:item.favorite="{ item }">
            <div v-if="overlay">
              <v-progress-circular color="primary" indeterminate />
            </div>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on }">
                <v-icon
                  :color="item.favorite ? 'warning' : 'grey'"
                  v-on="on"
                  @click="markAsFavorite(item)">
                  {{ item.favorite ? 'mdi-star' : 'mdi-star-outline' }}
                </v-icon>
              </template>
              <span>
                {{ item.favorite ? 'Desmarcar como favorito' : 'Marcar como favorito' }}
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.name="{ item }">
            <div style="max-width:200px">
              {{ item.name }}
              <!-- <div v-if="item.choice && item.choice.description" class="caption grey--text text-uppercase">
                {{ item.choice.description }}
              </div> -->
              <!-- <div
                v-if="item.choice && item.choice.brand"
                class="caption grey--text">
                Marca: {{ item.choice.brand }}
              </div> -->
            </div>
          </template>
          <template v-slot:item.choice.netTotal="{ item }">
            <div
              v-if="item.choice"
              class="text-right">
              <div>
                {{ (item.choice.netTotal ) | toFixNumber(2) | prefix('$') | numberWithComma }}
              </div>
              <div class="caption grey--text">
                1/{{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </div>
            </div>
            <div
              v-else
              class="text-right">
              <div>
                {{ item.product ? (item.product.cost / item.product.production) : 0 | toFixNumber(2) | prefix('$') | numberWithComma }}
              </div>
              <div class="caption grey--text">
                1/{{ item.unit.name }}
              </div>
            </div>
          </template>
          <template v-slot:item.quantityArchivedPast="{ item }">
            <div
              class="text-right">
              {{ (item.quantityArchivedPast || 0) | toFixNumber(2) | numberWithComma }}
              <span class="caption grey--text">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
          </template>
          <template v-slot:item.quantityArchived="{ item }">
            <div
              class="text-right">
              {{ item.quantityArchived | toFixNumber(2) | numberWithComma }}
              <span class="caption grey--text">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
          </template>
          <template v-slot:item.weeklyPurchase="{ item }">
            <div
              class="text-right">
              {{ item.weeklyPurchase | toFixNumber(2) | numberWithComma }}
              <span class="caption grey--text">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
          </template>
          <template
            v-slot:item.reorder="{ item }"
            v-if="['manager', 'stockmanager', 'buyer'].includes($is())">
            <div style="max-width:100px">
              <v-text-field
                v-model.number="item.reorder"
                type="number"
                label="Edit"
                single-line
                :rules="rules.numberValid"
                @update:error="setError('reorder', item, $event)"
              >
              </v-text-field>
            </div>
          </template>
          <template v-slot:item.category="{ item }">
            <div v-if="item.product && item.product.category">
              {{ item.product.category.name }}
            </div>
            <div v-else>
              SUBRECETA
            </div>
          </template>
          <template v-slot:item.stockQuantity="{ item }">
            <div class="text-right">
              <span>
                {{ item.stockQuantity | toFixNumber(3) | numberWithComma }}
              </span>
              <span class="caption grey--text">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
          </template>
          <template
            v-slot:item.weeklyDecrease="{ item }"
            v-if="['manager', 'stockmanager', 'buyer'].includes($is())">
            <div class="text-right" style="max-width:250px">
              <span>
                {{ item.weeklyDecrease | toFixNumber(2) | numberWithComma }}
              </span>
              <span class="caption">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
          </template>
          <template
            v-slot:item.consumption="{ item }"
            v-if="['manager', 'stockmanager', 'buyer'].includes($is())">
            <div class="text-right" style="max-width:250px">
              <a
                v-if="$is() !== 'stockmanager'"
                href="#"
                @click.prevent="openInfo(item)">
                {{ item.consumption | toFixNumber(2) | numberWithComma }}
              </a>
              <span v-else>
                {{ item.consumption | toFixNumber(2) | numberWithComma }}
              </span>
              <span class="caption">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
          </template>
          <template
            v-slot:item.quantityAvailable="{ item }"
            v-if="['manager', 'stockmanager', 'buyer'].includes($is())">
            <div v-if="item.updatedBy">
              <div class="text-right" style="max-width:250px">
                <span>
                  {{ item.quantityAvailable | toFixNumber(2) | numberWithComma }}
                </span>
                <span class="caption">
                  {{ ((item.choice || {}).contentUnit || {}).name || '' }}
                </span>
              </div>
              <div
                v-if="['manager', 'stockmanager', 'buyer'].includes($is())"
                class="text-right caption grey--text">
                {{ item.amountQuantityAvailable || 0 | toFixNumber(2) | prefix('$') | numberWithComma }}
              </div>
            </div>
            <div v-else>
              Sin captura
            </div>
          </template>
          <template v-slot:item.quantityCalculated="{ item }">
            <div class="text-right">
              <span>
                {{ item.quantityCalculated | toFixNumber(2) | numberWithComma }}
              </span>
              <span class="caption">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
            <div
              v-if="['manager', 'stockmanager', 'buyer'].includes($is())"
              class="text-right caption grey--text">
              {{ item.amountQuantityCalculated || 0 | toFixNumber(2) | prefix('$') | numberWithComma }}
            </div>
          </template>
          <template v-slot:item.difference="{ item }">
            <div class="text-right">
              <span>
                {{ item.difference | toFixNumber(2) | numberWithComma }}
              </span>
              <span class="caption">
                {{ ((item.choice || {}).contentUnit || {}).name || '' }}
              </span>
            </div>
            <div
              v-if="['manager', 'stockmanager', 'buyer'].includes($is())"
              class="text-right caption grey--text">
              {{ item.amountDifference || 0 | toFixNumber(2) | prefix('$') | numberWithComma }}
            </div>
          </template>
        </v-data-table>
      </v-form>
    </v-flex>

    <v-dialog
      @keydown.esc="cleanDialog=false"
      v-model="cleanDialog"
      max-width="500"
    >
      <v-card
        color="blue-grey"
        dark
      >
        <v-card-title>
          Limpiar inventario
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col md="1">
              <v-icon>mdi-alert</v-icon>
            </v-col>
            <v-col>
              <div>
                Al aceptar, los insumos que no están relacionados a ninguna receta serán eliminados junto con su inventario.
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            text
           @click="cleanDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="blue-grey darken-4"
           @click="cleanInventory">
            De acuerdo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConsuptionDetail ref="consuptionDetail" />
  </div>
</template>

<script>
import _ from 'lodash'
import ConsuptionDetail from '@/components/sidePanels/ConsuptionDetail.vue'

export default {
  name: 'Inventory',
  components: {
    ConsuptionDetail
  },
  data () {
    return {
      isValid: false,
      cleanDialog: false,
      overlay: false,
      section: this.$i18n.t('inventory.listTitle'),
      sortAttrName: 'name',
      attrNamesSorteable: ['name', 'choice.provider.name', 'area.name', 'difference'],
      loadedData: null,
      table: {
        title: this.$i18n.t('inventory.loading'),
        loading: true,
        headers: [
          { value: 'favorite', sortable: false },
          { text: this.$i18n.t('inventory.headers.name'), value: 'name' },
          { text: this.$i18n.t('inventory.headers.departament'), value: 'category' },
          { text: this.$i18n.t('inventory.headers.price'), value: 'choice.netTotal', sortable: false },
          { text: this.$i18n.t(`inventory.headers.stock`), value: 'stockQuantity', sortable: false },
          { text: this.$i18n.t('inventory.headers.quantityArchivedPast'), value: 'quantityArchivedPast', sortable: false },
          { text: this.$i18n.t('inventory.headers.quantityArchived'), value: 'quantityArchived', sortable: false },
          { text: this.$i18n.t('inventory.headers.weeklyPurchase'), value: 'weeklyPurchase', sortable: false },
          { text: this.$i18n.t('inventory.headers.weeklyDecrease'), value: 'weeklyDecrease', sortable: false },
          { text: this.$i18n.t(`inventory.headers.consume`), value: 'consumption', sortable: false },
          { text: this.$i18n.t(`inventory.headers.quantityAvailable.${this.$is()}`), value: 'quantityAvailable', sortable: false },
          { text: this.$i18n.t('inventory.headers.quantityCalculated'), value: 'quantityCalculated', sortable: false },
          { text: this.$i18n.t(`inventory.headers.difference`), value: 'difference', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          itemsPerPage: 100,
          page: 1
        },
        footer: {
          'items-per-page-options': [50, 100, 200]
        }
      },
      info: {
        activated: false,
        loading: false,
        item: null,
        data: {
          recipes: null,
          movements: null
        }
      },
      categories: [],
      category: null,
      search: '',
      errors: [],
      rules: {
        numberValid: [
          v => (v !== '' && !isNaN(v * 1)) || this.$t('validations.numberValid')
        ]
      },
      favorites: 0
    }
  },
  computed: {
    exportRoute () {
      return `${process.env.VUE_APP_API_URL}/v1/inputs/export?restaurant=${this.$store.state.currentRestaurant.data._id}`
    },
    headers () {
      const allowedHeaders = this.table.headers
      // if (this.$is() === 'stockmanager') {
      //   const restrictedHeaders = ['reorder', 'quantityCalculated', 'difference']
      //   restrictedHeaders.forEach((restrictedHeader) => {
      //     const index = allowedHeaders.findIndex(header => header.value === restrictedHeader)
      //     allowedHeaders.splice(index, 1)
      //   })
      // }
      // if (this.$is() === 'manager' || this.$is() === 'buyer') {
      //   const restrictedHeaders = ['stockArea', 'category']
      //   restrictedHeaders.forEach((restrictedHeader) => {
      //     const index = allowedHeaders.findIndex(header => header.value === restrictedHeader)
      //     allowedHeaders.splice(index, 1)
      //   })
      // }
      return allowedHeaders
    },
    inventoryAt () {
      if (this.table.data.length > 0) {
        return this.table.data[0].updatedQuantityAvailableAt || null
      }
      return null
    },
    inventoryBy () {
      if (this.table.data.length > 0) {
        return this.table.data[0].updatedQuantityAvailableBy ? this.table.data[0].updatedQuantityAvailableBy.email : null
      }
      return null
    },
    area () {
      return this.$store.state.area.id || null
    }
  },
  watch: {
    'search': async function (val) {
      const params = {
        limit: this.table.options.itemsPerPage,
        area: this.area,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1)),
        sort: 'name'
      }
      if (val.length >= 3) {
        try {
          params.name = new RegExp(val, 'i')
          let content = this.loadedData
          if (this.category) {
            params['product.category'] = this.category
            content = content.filter(x => {
              if (x.product && x.product.category) {
                return x.product.category.uuid === params['product.category']
              }
              return false
            })
          }
          content = content.filter(x => params.name.test(x.name))
          this.table.data = content
          this.table.total = content.length
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      } else {
        this.filterByCategory(this.category)
      }
    },
    favorites: async function (val) {
      this.search = ''
      if (this.loadedData.length) {
        this.filterByCategory(this.category)
      }
    }
  },
  async mounted () {
    await this.getCategories()
    if (this.$store.state.area) {
      await this.load({
        limit: -1,
        skip: 0,
        sort: 'name',
        area: this.area
      })
    }
  },
  methods: {
    updateInput (input) {
      const index = this.loadedData.findIndex(item => item._id === input._id)
      const copyData = [...this.loadedData]
      copyData[index].favorite = input.favorite
      this.loadedData = copyData
      this.table.data = this.loadedData
      this.filterByCategory(this.category)
    },
    markAsFavorite: async function (item) {
      this.overlay = true
      const payload = { favorite: !item.favorite }
      try {
        const input = await this.$api.put(`/v1/inputs/${item._id}/set`, payload)
        this.updateInput(input)
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    showFavoriteData () {
      this.table.data = this.loadedData.filter(item => item.favorite)
      this.table.total = this.table.data.length
    },
    setError (type, item, error) {
      if (error) {
        const index = this.errors.findIndex(i => i.uuid === item.uuid)
        if (index < 0) {
          this.errors.push(item)
        }
      } else {
        const index = this.errors.findIndex(i => i.uuid === item.uuid)
        if (index >= 0) {
          this.errors.splice(index, 1)
        }
      }
    },
    convertToDateString (date) {
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    openInfo (item) {
      this.$refs.consuptionDetail.open(item)
    },
    print () {
      window.print()
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      const start = data.itemsPerPage * (data.page - 1)
      const end = data.itemsPerPage * data.page
      const sortParam = (sortDesc > 0 ? '' : '-') + sortBy
      this.table.data = _.sortBy(this.loadedData, sortParam).slice(start, end)
    },
    async load (params = null) {
      params = params || {
        area: this.area,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: 'name'
      }

      params.limit = 1000
      this.overlay = true
      try {
        const response = await this.$api.get('/v1/inputs', params)

        this.loadedData = response.data.map(input => {
          const quantityCalculated = (input.quantityArchived + input.weeklyPurchase) - input.consumption - input.weeklyDecrease
          const difference = input.quantityAvailable - quantityCalculated
          let amountQuantityCalculated, amountQuantityAvailable, amountDifference
          if (input.choice) {
            amountQuantityAvailable = input.quantityAvailable / input.choice.contentQuantity * input.choice.amount
            amountQuantityCalculated = (input.choice.netTotal / input.choice.contentQuantity) * quantityCalculated
            amountDifference = (difference / input.choice.contentQuantity) * input.choice.amount
          } else {
            // amountQuantityAvailable = input.quantityAvailable * ((input.product || {}).cost || 0)
            // amountQuantityCalculated = (input.product ? (input.product.cost / input.product.production) : 0) * quantityCalculated
            // amountDifference = difference * (input.product || {}).cost || 0
          }
          return Object.assign(
            input,
            {
              quantityCalculated,
              amountQuantityCalculated,
              amountQuantityAvailable,
              amountDifference,
              difference,
              quantityAvailable: (this.$is() === 'stockmanager') ? '' : input.quantityAvailable
            }
          )
        })
        this.table.data = this.loadedData

        await this.updateTable({
          sortBy: 'name',
          sortDesc: 1,
          itemsPerPage: this.table.options.itemsPerPage,
          page: this.table.options.page
        })
        this.table.total = response.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    async update () {
      this.$refs.inventory.validate()
      this.loadedData.forEach(item => {
        if (item.quantityAvailable === '') {
          this.setError('quantityAvailable', item, true)
        }
      })
      if (!this.isValid || this.errors.length) {
        const errors = this.errors.map(error => `</br>${error.name}`)
        const message = `Existen datos vacios en los productos: ${errors}`
        this.$toasted.show(
          message,
          {
            action: {
              text: 'Cerrar',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
              }
            }
          }
        )
      } else {
        this.overlay = true
        const payload = {
          items: this.loadedData.map(item => ({
            quantityCalculated: item.quantityCalculated,
            quantity: item.quantityAvailable,
            id: item.uuid,
            reorder: item.reorder
          }))
        }
        try {
          await this.$api.put('/v1/inputs/update', payload)
          this.$toasted.show(
            'Los productos se actualizaron correctamente',
            {
              type: 'success',
              action: {
                class: 'action-black',
                text: 'Cerrar',
                onClick: (e, toastObject) => {
                  toastObject.goAway(0)
                }
              }
            })
          this.$refs.inventory.reset()
          await this.load({
            limit: -1,
            skip: 0,
            sort: 'name',
            area: this.area,
            forceReload: true
          })
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.table.options.page = 1
        this.category = null
        this.search = ''
        this.overlay = false
      }
    },
    async getCategories () {
      const { data } = await this.$api.get(`/v1/categories`, { sort: 'name' })
      data.unshift({ id: null, name: 'Todas' })
      this.categories = data
    },
    onChangeCategory (category) {
      this.search = ''
      this.filterByCategory(category)
    },
    filterByCategory (category) {
      this.category = category
      const params = {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1)),
        sort: 'name',
        area: this.area
      }
      this.overlay = true
      if (category) {
        params['product.category'] = category
        const content = this.loadedData.filter(x => {
          if (x.product && x.product.category) {
            return this.favorites === 1
              ? x.product.category.id === params['product.category'] && x.favorite
              : x.product.category.id === params['product.category']
          }
          return false
        })
        this.table.data = content
        this.table.total = content.length
      } else {
        if (this.favorites === 1) {
          this.showFavoriteData()
        } else {
          this.table.data = this.loadedData
          this.table.total = this.loadedData.length
        }
      }
      this.overlay = false
    },
    openResetTeoricInventoriDialog () {
      this.resetDialog = true
    },
    async cleanInventory () {
      this.cleanDialog = false
      this.overlay = true
      try {
        await this.$api.post('/v1/inputs/bulk-clean', { area: this.area })
        this.$toasted.show('El inventario se actualizó correctamente', { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }

      await this.load({
        limit: this.table.options.itemsPerPage,
        skip: 0,
        sort: 'name',
        area: this.area
      })
      this.overlay = false
    },
    async sortDescTable (data) {
      if (!data) return
      this.loadedData = this.loadedData.reverse()
      await this.updateTable({
        sortBy: this.sortAttrName,
        sortDesc: -1,
        itemsPerPage: this.table.options.itemsPerPage,
        page: this.table.options.page
      })
    },
    async sortByTable (data) {
      if (!data && !this.attrNamesSorteable.includes(data)) return
      const floatColumns = [ 'difference' ]
      this.loadedData = this.loadedData.sort((a, b) => {
        a = _.get(a, data)
        b = _.get(b, data)
        if (floatColumns.includes(data)) {
          a = parseFloat(a)
          b = parseFloat(b)
        }
        if (a > b) return 1
        if (a < b) return -1
        return 0
      })
      this.sortAttrName = data
      await this.updateTable({
        sortBy: data,
        sortDesc: 1,
        itemsPerPage: this.table.options.itemsPerPage,
        page: this.table.options.page,
        area: this.area
      })
    },
    calculateTotal (attr) {
      const total = this.table.data.reduce((acc, val) => {
        return acc + (val[attr] || 0)
      }, 0)
      return total
    }
  }
}
</script>

<style lang="scss">
.action-black {
  color: black !important;
}
.z-index--100 {
  z-index: 100 !important;
}

.horizontal-scroll {
  overflow-x: auto;
}
</style>
